import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import sendSVG from '@plone/volto/icons/send.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import attachmentSVG from '@plone/volto/icons/attachment.svg';
import showSVG from '@plone/volto/icons/show.svg';
import penSVG from '@plone/volto/icons/pen.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import blockSVG from '@plone/volto/icons/block.svg';
import flashSVG from '@plone/volto/icons/flash.svg';

import type { Skill, SkillGroup } from '../../types';
import SkillPicker from './SkillPicker';

const messages = defineMessages({
  placeholder: {
    id: 'Ask about this page or request a change…',
    defaultMessage: 'Ask about this page or request a change…',
  },
  allowChanges: {
    id: 'assistantAllowChanges',
    defaultMessage: 'Allow changes',
  },
  writeHint: {
    id: 'assistantWriteHint',
    defaultMessage: 'When on, this message may change the page',
  },
  send: {
    id: 'Send',
    defaultMessage: 'Send',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  attach: {
    id: 'Attach image',
    defaultMessage: 'Attach image',
  },
  followChanges: {
    id: 'assistantFollowChanges',
    defaultMessage: 'Follow changes',
  },
  followChangesHint: {
    id: 'assistantFollowChangesHint',
    defaultMessage: 'Scroll the page to each change while the assistant works',
  },
  pickReference: {
    id: 'assistantPickReference',
    defaultMessage: 'Reference site content',
  },
  pickReferenceHint: {
    id: 'assistantPickReferenceHint',
    defaultMessage: 'Pick content from the site to reference in this message',
  },
  removeReference: {
    id: 'Remove reference',
    defaultMessage: 'Remove reference',
  },
  removeAttachment: {
    id: 'Remove attachment',
    defaultMessage: 'Remove attachment',
  },
  removeSkill: {
    id: 'Remove action',
    defaultMessage: 'Remove action',
  },
  selectedBlock: {
    id: 'Selected block',
    defaultMessage: 'Selected block',
  },
  showBlockHint: {
    id: 'assistantShowBlockHint',
    defaultMessage: 'Show this block on the page',
  },
  dismissContext: {
    id: 'Ignore selection',
    defaultMessage: 'Ignore selection',
  },
  skills: {
    id: 'assistantSkills',
    defaultMessage: 'Actions',
  },
  skillsHint: {
    id: 'assistantSkillsHint',
    defaultMessage:
      'Things the assistant can do for you — pick one to send with your message',
  },
});

/**
 * Message composer: text input with image attachments (file picker or
 * clipboard paste), site-content references picked via Volto's native
 * object browser, skills picked from the agent's catalogue (editors see them
 * as actions; shown as chips, sent by name beside the text), the per-turn write-permission toggle (the
 * agent's signature interaction), and a dismissible context chip showing the
 * block currently selected in the edit canvas.
 */
const Composer = ({
  running,
  permission,
  onSetPermission,
  contextBlock,
  onFocusContext,
  onDismissContext,
  followChanges,
  onSetFollowChanges,
  draft,
  onChangeDraft,
  references = [],
  onToggleReference,
  pickedSkills = [],
  onAddSkill,
  onRemoveSkill,
  onSend,
  onCancel,
  openObjectBrowser,
  skills = [],
  skillGroups = [],
}) => {
  const intl = useIntl();
  // Block types are registered with an untranslated title; Volto's own
  // chooser translates it by using the title as the message id.
  const contextBlockTitle = contextBlock
    ? intl.formatMessage({
        id: contextBlock.title,
        defaultMessage: contextBlock.title,
      })
    : '';
  const [text, setText] = React.useState(draft?.text || '');
  const [skillsOpen, setSkillsOpen] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [attachments, setAttachments] = React.useState(
    draft?.attachments || [],
  );
  // Image files still being read — sending waits for them.
  const [pendingReads, setPendingReads] = React.useState(0);
  const fileInputRef = React.useRef(null);

  // The draft survives the composer remounting (the write toggle navigates
  // from view mode into edit mode): mirror it into the store as it
  // changes; only settled attachments travel — a reader cannot.
  React.useEffect(() => {
    onChangeDraft?.({
      text,
      attachments: attachments.filter((attachment) => attachment.dataUrl),
    });
  }, [text, attachments, onChangeDraft]);

  const addFiles = (files: Iterable<File>) => {
    const images = Array.from(files).filter((file) =>
      file.type.startsWith('image/'),
    );
    if (!images.length) return;
    // Each file claims its slot up front, in selection order; the slot
    // fills in (or drops out) when its read settles.
    setPendingReads((count) => count + images.length);
    images.forEach((file) => {
      const slot = { name: file.name || 'pasted image', dataUrl: null };
      setAttachments((prev) => [...prev, slot]);
      const reader = new FileReader();
      const settle = (dataUrl: string | null) => {
        setAttachments((prev) =>
          dataUrl
            ? prev.map((candidate) =>
                candidate === slot ? { ...candidate, dataUrl } : candidate,
              )
            : prev.filter((candidate) => candidate !== slot),
        );
        setPendingReads((count) => count - 1);
      };
      reader.onload = () => settle(reader.result as string);
      reader.onerror = () => settle(null);
      reader.readAsDataURL(file);
    });
  };

  const onPaste = (event: React.ClipboardEvent) => {
    const files = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file && file.type.startsWith('image/'));
    if (files.length > 0) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (
      (!trimmed &&
        attachments.length === 0 &&
        references.length === 0 &&
        pickedSkills.length === 0) ||
      pendingReads > 0 ||
      running
    )
      return;
    // Pending references are owned by the panel and merged in onSend.
    onSend(trimmed, { attachments });
    setText('');
    setAttachments([]);
  };

  const write = permission === 'write';

  return (
    <div className="assistant-composer">
      {(contextBlock || references.length > 0 || pickedSkills.length > 0) && (
        <div className="assistant-composer-chips">
          {contextBlock && (
            <div className="assistant-composer-context">
              <button
                type="button"
                className="assistant-composer-context-label"
                aria-label={`${intl.formatMessage(messages.selectedBlock)}: ${contextBlockTitle}`}
                title={intl.formatMessage(messages.showBlockHint)}
                onClick={onFocusContext}
              >
                <Icon name={contextBlock.icon || blockSVG} size="14px" />
                <span>{contextBlockTitle}</span>
              </button>
              <button
                type="button"
                className="assistant-composer-context-dismiss"
                aria-label={intl.formatMessage(messages.dismissContext)}
                title={intl.formatMessage(messages.dismissContext)}
                onClick={onDismissContext}
              >
                <Icon name={clearSVG} size="12px" />
              </button>
            </div>
          )}
          {pickedSkills.map((skill: Skill) => (
            <div
              key={skill.name}
              className="assistant-composer-reference assistant-composer-skill"
              title={skill.description}
            >
              <Icon name={flashSVG} size="14px" />
              <span>{skill.title}</span>
              <button
                type="button"
                aria-label={`${intl.formatMessage(messages.removeSkill)}: ${
                  skill.title
                }`}
                title={intl.formatMessage(messages.removeSkill)}
                onClick={() => onRemoveSkill(skill.name)}
              >
                <Icon name={clearSVG} size="12px" />
              </button>
            </div>
          ))}
          {references.map((reference) => (
            <div
              key={reference['@id']}
              className="assistant-composer-reference"
              title={reference['@id']}
            >
              <Icon name={linkSVG} size="14px" />
              <span>{reference.title}</span>
              <button
                type="button"
                aria-label={intl.formatMessage(messages.removeReference)}
                title={intl.formatMessage(messages.removeReference)}
                onClick={() =>
                  onToggleReference(reference['@id'], {
                    title: reference.title,
                    '@type': reference.type,
                  })
                }
              >
                <Icon name={clearSVG} size="12px" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="assistant-composer-box">
        {attachments.length > 0 && (
          <div className="assistant-composer-attachments">
            {attachments.map((attachment, index) => (
              <div key={index} className="assistant-attachment">
                {attachment.dataUrl ? (
                  <Image src={attachment.dataUrl} alt={attachment.name} />
                ) : (
                  <span
                    className="assistant-attachment-loading"
                    aria-label={attachment.name}
                  />
                )}
                <button
                  type="button"
                  aria-label={intl.formatMessage(messages.removeAttachment)}
                  title={intl.formatMessage(messages.removeAttachment)}
                  onClick={() =>
                    setAttachments((prev) =>
                      prev.filter((candidate, i) => i !== index),
                    )
                  }
                >
                  <Icon name={clearSVG} size="10px" />
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          rows={2}
          placeholder={intl.formatMessage(messages.placeholder)}
          onChange={(event) => setText(event.target.value)}
          onPaste={onPaste}
          onKeyDown={(event) => {
            // Enter during IME composition confirms the candidate — it
            // must never send the half-composed draft.
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className="assistant-composer-footer">
          <div className="assistant-composer-buttons">
            {skills.length > 0 && (
              <button
                type="button"
                className={`assistant-composer-skills${
                  skillsOpen ? ' on' : ''
                }`}
                aria-label={intl.formatMessage(messages.skills)}
                aria-expanded={skillsOpen}
                aria-haspopup="dialog"
                title={intl.formatMessage(messages.skillsHint)}
                onClick={() => setSkillsOpen(true)}
              >
                <Icon name={flashSVG} size="18px" />
              </button>
            )}
            <button
              type="button"
              role="switch"
              aria-checked={write}
              className={`assistant-composer-write${write ? ' on' : ''}`}
              aria-label={intl.formatMessage(messages.allowChanges)}
              title={intl.formatMessage(messages.writeHint)}
              onClick={() => onSetPermission(write ? 'read' : 'write')}
            >
              <Icon name={penSVG} size="18px" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = '';
              }}
            />
            {onSetFollowChanges && (
              <button
                type="button"
                role="switch"
                aria-checked={followChanges}
                className={`assistant-composer-follow${
                  followChanges ? ' on' : ''
                }`}
                aria-label={intl.formatMessage(messages.followChanges)}
                title={intl.formatMessage(messages.followChangesHint)}
                onClick={() => onSetFollowChanges(!followChanges)}
              >
                <Icon name={showSVG} size="18px" />
              </button>
            )}
            <button
              type="button"
              className="assistant-composer-pick"
              aria-label={intl.formatMessage(messages.pickReference)}
              title={intl.formatMessage(messages.pickReferenceHint)}
              onClick={() =>
                openObjectBrowser({
                  mode: 'multiple',
                  propDataName: 'references',
                  overlay: true,
                  onSelectItem: onToggleReference,
                })
              }
            >
              <Icon name={linkSVG} size="18px" />
            </button>
            <button
              type="button"
              className="assistant-composer-attach"
              aria-label={intl.formatMessage(messages.attach)}
              title={intl.formatMessage(messages.attach)}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name={attachmentSVG} size="18px" />
            </button>
            {running ? (
              <button
                type="button"
                className="assistant-composer-cancel"
                aria-label={intl.formatMessage(messages.cancel)}
                title={intl.formatMessage(messages.cancel)}
                onClick={onCancel}
              >
                <span className="assistant-composer-stop" />
              </button>
            ) : (
              <button
                type="button"
                className="assistant-composer-send"
                aria-label={intl.formatMessage(messages.send)}
                disabled={
                  pendingReads > 0 ||
                  (!text.trim() &&
                    attachments.length === 0 &&
                    references.length === 0 &&
                    pickedSkills.length === 0)
                }
                onClick={submit}
              >
                <Icon name={sendSVG} size="18px" />
              </button>
            )}
          </div>
        </div>
      </div>
      <SkillPicker
        skills={skills as Skill[]}
        groups={skillGroups as SkillGroup[]}
        open={skillsOpen}
        onClose={() => setSkillsOpen(false)}
        onPick={(skill) => {
          // Attached, not sent: the editor finishes the message themselves.
          onAddSkill(skill);
          textareaRef.current?.focus();
        }}
      />
    </div>
  );
};

export default withObjectBrowser(Composer);
